import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';

/**
 * AILoadingOverlay — rebuilt (Sep 2026)
 *
 * The old overlay was theatre: stage chips ("Outlier Detection") rendered for
 * checks the user never selected, and the terminal printed invented findings
 * ("Missingness rate: 96.5%", "Duplicate: Found 554 matches"). This version
 * shows only what is true:
 *   - a Claude-style shimmering status line cycling through the checks that
 *     are ACTUALLY running (passed in via `tasks`)
 *   - a live-activity feed with real facts only: dataset shape, the queued
 *     detectors, and elapsed-time heartbeats
 * Removed by design: stage pills, progress bar, "discoveries", traffic-light
 * dots. Kept: the drifting background specks and the terminal feed.
 *
 * Visual language matches ProjectLoadingOverlay (same background gradient,
 * tokens, and type) so the two loaders read as one family.
 *
 * Props
 *   isVisible        render gate
 *   isDarkMode       theme (defaults dark, like the analysis screens)
 *   title            heading, e.g. "Running quality checks"
 *   subtitle         one quiet line under the heading (optional)
 *   tasks            [{ id, label, detail }] — ONLY the selected checks
 *   realTimeStats    { totalRows, columnsAnalyzed } — real numbers, shown as-is
 *   showDetailedLogs show/hide the live-activity feed (default true)
 *   embedded         position:absolute inside a container instead of fullscreen
 *   context          accepted for backwards compatibility; unused
 */
// Runs survive remounts: when the user clicks "resume work" while a run is in
// flight, the preview-mounted overlay unmounts and a fresh one mounts in the
// full workspace. The run itself lives in App state and keeps going; this
// registry lets the new overlay adopt the old one's start time and feed so
// the clock and activity log continue instead of restarting from zero.
const RUN_REGISTRY = new Map();
const ADOPT_WINDOW_MS = 8000; // only adopt a run another instance touched recently

const AILoadingOverlay = ({
  isVisible = false,
  isDarkMode: isDarkModeProp,
  title = 'Running quality checks',
  subtitle = null,
  tasks = [],
  realTimeStats = {},
  showDetailedLogs = true,
  embedded = false,
  persistKey = null,
  context, // eslint-disable-line no-unused-vars
}) => {
  const uid = useMemo(() => 'ailo-' + Math.random().toString(36).slice(2, 8), []);
  // Follow the live app theme; an explicit prop still wins. This keeps the
  // overlay in sync when the user flips light/dark WHILE a run is in flight,
  // and fixes call sites that never passed isDarkMode (they inherited a
  // hardcoded dark default before).
  const themeCtx = useTheme() || {};
  const isDarkMode = typeof isDarkModeProp === 'boolean' ? isDarkModeProp : (themeCtx.isDarkMode ?? true);
  const [elapsed, setElapsed] = useState(0);
  const [statusIdx, setStatusIdx] = useState(0);
  const [feed, setFeed] = useState([]);
  const feedRef = useRef(null);
  const startRef = useRef(null);

  const rows = Number(realTimeStats?.totalRows) || 0;
  const cols = Number(realTimeStats?.columnsAnalyzed) || 0;

  // Truthful rotating status lines: dataset first, then each selected check.
  const statusLines = useMemo(() => {
    const lines = [];
    if (rows || cols) {
      lines.push(`Reading ${rows.toLocaleString()} rows across ${cols.toLocaleString()} variables`);
    }
    (tasks || []).forEach((t) => {
      lines.push(`Checking ${t.label}${t.detail ? ` \u00b7 ${t.detail}` : ''}`);
    });
    if (!lines.length) lines.push('Processing your data');
    return lines;
  }, [rows, cols, tasks]);

  // Start (or adopt) the run whenever the overlay opens
  useEffect(() => {
    if (!isVisible) return undefined;
    const key = persistKey || title || 'ailo-run';
    const now = Date.now();
    const existing = RUN_REGISTRY.get(key);
    const adopting = !!(existing && now - (existing.lastSeen || 0) < ADOPT_WINDOW_MS);
    const entry = adopting ? existing : { startedAt: now, feed: [], lastSeen: now };
    RUN_REGISTRY.set(key, entry);

    startRef.current = entry.startedAt;
    const secondsIn = Math.floor((now - entry.startedAt) / 1000);
    setElapsed(secondsIn);
    setStatusIdx(adopting ? Math.floor((now - entry.startedAt) / 2600) : 0);
    setFeed(adopting ? entry.feed.slice() : []);

    const stamp = () => {
      const s = Math.floor((Date.now() - startRef.current) / 1000);
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    };
    const append = (level, text) => {
      setFeed((f) => {
        const last = f[f.length - 1];
        if (last && last.text === text) return f;
        const nf = [...f, { stamp: stamp(), level, text }];
        entry.feed = nf;
        return nf;
      });
    };

    // Seed the feed with real facts, staggered so it reads as activity.
    // Skipped when adopting a run that already logged them.
    let timers = [];
    if (!adopting) {
      const seed = [
        { t: 0, level: 'info', text: 'session started' },
        ...(rows || cols
          ? [{ t: 180, level: 'info', text: `dataset: ${rows.toLocaleString()} rows \u00d7 ${cols.toLocaleString()} variables` }]
          : []),
        ...(tasks || []).map((task, i) => ({
          t: 420 + i * 260,
          level: 'task',
          text: `queued ${task.label}${task.detail ? ` \u2014 ${task.detail}` : ''}`,
        })),
        { t: 420 + (tasks?.length || 0) * 260 + 400, level: 'info', text: 'request sent \u2014 engine is processing' },
      ];
      timers = seed.map((line) => setTimeout(() => append(line.level, line.text), line.t));
    }

    const tick = setInterval(() => {
      const s = Math.floor((Date.now() - startRef.current) / 1000);
      setElapsed(s);
      entry.lastSeen = Date.now();
      // Honest heartbeat instead of invented findings
      if (s > 0 && s % 8 === 0) {
        append('dim', `still processing \u2014 ${s}s elapsed`);
      }
    }, 1000);

    const cycle = setInterval(() => setStatusIdx((i) => i + 1), 2600);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(tick);
      clearInterval(cycle);
      // Leave the registry entry: an unmount mid-run (preview -> expanded)
      // must let the successor instance adopt it.
      entry.lastSeen = Date.now();
    };
  }, [isVisible]);

  // The run has genuinely finished when the overlay is told to hide:
  // clear the registry so the next run starts a fresh clock.
  useEffect(() => {
    if (isVisible) return;
    const key = persistKey || title || 'ailo-run';
    RUN_REGISTRY.delete(key);
  }, [isVisible, persistKey, title]);

  // Keep the feed scrolled to the newest line
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [feed]);

  if (!isVisible) return null;

  const dark = !!isDarkMode;
  const tokens = dark
    ? {
        bgGrad: 'radial-gradient(ellipse 80% 60% at 50% 45%, #11141A 0%, #0A0B0D 60%, #07080A 100%)',
        heading: '#E7EAF0',
        msg: '#B8BDC9',
        muted: '#6B7180',
        dim: '#3A3F4A',
        card: 'rgba(255,255,255,0.03)',
        cardBorder: 'rgba(255,255,255,0.07)',
        mono: '#9AA3B2',
        task: '#7DD3FC',
        speck: 'rgba(148, 163, 184, 0.35)',
      }
    : {
        bgGrad: 'radial-gradient(ellipse 80% 60% at 50% 45%, #FFFFFF 0%, #F2F4F7 60%, #E9ECF1 100%)',
        heading: '#1F2430',
        msg: '#3A3F4A',
        muted: '#6B7180',
        dim: '#A8AEBA',
        card: 'rgba(255,255,255,0.65)',
        cardBorder: 'rgba(0,0,0,0.07)',
        mono: '#4B5563',
        task: '#0369A1',
        speck: 'rgba(100, 116, 139, 0.3)',
      };

  const currentStatus = statusLines[statusIdx % statusLines.length];

  return (
    <div
      className={`ailo-root ${uid}`}
      role="status"
      aria-live="polite"
      aria-label={title}
      style={{
        position: embedded ? 'absolute' : 'fixed',
        inset: 0,
        // Inside a CSS-transformed ancestor (the active-workspace preview),
        // fixed positioning resolves against that ancestor and the box can
        // collapse; these minimums keep the backdrop full-bleed there too.
        minWidth: embedded ? '100%' : '100vw',
        minHeight: embedded ? '100%' : '100vh',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.bgGrad,
        fontFamily: "'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        color: tokens.msg,
        overflow: 'hidden',
      }}
    >
      {/* Drifting background specks (kept from the old overlay) */}
      {[...Array(10)].map((_, i) => (
        <span key={i} className="ailo-speck" style={{
          left: `${(i * 89) % 100}%`,
          top: `${(i * 37) % 100}%`,
          animationDelay: `${i * 1.7}s`,
          animationDuration: `${16 + (i % 5) * 4}s`,
        }} />
      ))}

      <div style={{ width: 'min(640px, calc(100% - 48px))', position: 'relative' }}>
        {/* Heading */}
        <div style={{ marginBottom: '6px', fontSize: '22px', fontWeight: 500, letterSpacing: '-0.015em', color: tokens.heading }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: '13px', color: tokens.muted, marginBottom: '2px' }}>{subtitle}</div>
        )}

        {/* Claude-style shimmering "what I'm doing" line */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', margin: '18px 0 22px', minHeight: '22px' }}>
          <span className="ailo-shimmer" key={statusIdx} style={{ fontSize: '14.5px', fontWeight: 450 }}>
            {currentStatus}
            <span className="ailo-ellipsis" aria-hidden="true" />
          </span>
          <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: tokens.dim, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {elapsed}s
          </span>
        </div>

        {/* Live activity — real events only */}
        {showDetailedLogs && (
          <div style={{
            borderRadius: '12px',
            background: tokens.card,
            border: `0.5px solid ${tokens.cardBorder}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 14px',
              borderBottom: `0.5px solid ${tokens.cardBorder}`,
            }}>
              <span style={{ fontSize: '10px', letterSpacing: '0.18em', textTransform: 'lowercase', color: tokens.muted, fontFamily: "'JetBrains Mono', 'SF Mono', ui-monospace, monospace" }}>
                {'live activity \u2014 dataquad'}
              </span>
              <span className="ailo-live-dot" aria-hidden="true" />
            </div>
            <div ref={feedRef} style={{
              maxHeight: '240px', overflowY: 'auto',
              padding: '12px 14px 14px',
              fontFamily: "'JetBrains Mono', 'SF Mono', ui-monospace, monospace",
              fontSize: '11.5px', lineHeight: 1.9,
            }}>
              {feed.map((line, i) => (
                <div key={i} className="ailo-line" style={{
                  color: line.level === 'task' ? tokens.task : line.level === 'dim' ? tokens.dim : tokens.mono,
                }}>
                  <span style={{ color: tokens.dim, marginRight: '10px' }}>[{line.stamp}]</span>
                  {line.text}
                </div>
              ))}
              <div style={{ color: tokens.dim }}>
                <span className="ailo-caret" aria-hidden="true">{'\u258e'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .${uid} .ailo-speck {
          position: absolute;
          width: 3px; height: 3px; border-radius: 50%;
          background: ${tokens.speck};
          animation: ${uid}-drift linear infinite;
          pointer-events: none;
        }
        @keyframes ${uid}-drift {
          0%   { transform: translate(0, 0); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translate(40px, -120px); opacity: 0; }
        }
        .${uid} .ailo-shimmer {
          background: linear-gradient(90deg, ${tokens.muted} 0%, ${tokens.heading} 45%, ${tokens.muted} 90%);
          background-size: 220% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: ${uid}-shimmer 2.2s ease-in-out infinite, ${uid}-fadein 0.45s ease;
        }
        @keyframes ${uid}-shimmer {
          0% { background-position: 120% 0; }
          100% { background-position: -120% 0; }
        }
        @keyframes ${uid}-fadein {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: none; }
        }
        .${uid} .ailo-ellipsis::after {
          content: '';
          animation: ${uid}-dots 1.6s steps(4, end) infinite;
        }
        @keyframes ${uid}-dots {
          0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; }
        }
        .${uid} .ailo-live-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #10B981;
          box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
          animation: ${uid}-pulse 2.2s ease-in-out infinite;
        }
        @keyframes ${uid}-pulse {
          0%, 100% { opacity: 1; } 50% { opacity: 0.35; }
        }
        .${uid} .ailo-line { animation: ${uid}-fadein 0.3s ease; }
        .${uid} .ailo-caret { animation: ${uid}-blink 1.1s steps(1) infinite; }
        @keyframes ${uid}-blink { 50% { opacity: 0; } }
        @media (prefers-reduced-motion: reduce) {
          .${uid} .ailo-speck, .${uid} .ailo-caret, .${uid} .ailo-live-dot { animation: none !important; }
          .${uid} .ailo-shimmer {
            animation: none !important;
            -webkit-text-fill-color: ${tokens.heading};
          }
        }
      `}</style>
    </div>
  );
};

export default AILoadingOverlay;